import { useCallback, useEffect, useState } from 'react';
import { Plus, Sparkles, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { LandingExperimentPanel } from './LandingExperimentPanel';

interface PageSummary {
  id: string;
  slug: string;
  name: string;
  status: string;
  formId: string | null;
  defaultLocale: string;
  abEnabled: boolean;
  customDomain: string | null;
}

interface ContentRow {
  locale: string;
  abVariant: string;
  heroHeadline: string;
  heroImageUrl: string | null;
  bodyMd: string | null;
  thankYouMessage: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImageUrl: string | null;
}

interface PageDetail extends PageSummary {
  content: ContentRow[];
}

interface FormOption {
  id: string;
  title: string;
}

const LOCALES = ['es', 'en', 'pt-BR'] as const;
const VARIANTS = ['a', 'b'] as const;

export function CampaignLandingPagesPage() {
  const { t } = useTranslation();
  const [pages, setPages] = useState<PageSummary[]>([]);
  const [forms, setForms] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PageDetail | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiClient.get<PageSummary[]>('/landing-pages'),
      apiClient.get<FormOption[]>('/lead-capture-forms'),
    ])
      .then(([p, f]) => {
        setPages(p.data);
        setForms(
          f.data.map((x) => ({ id: x.id, title: (x as { title: string }).title })),
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => reload(), [reload]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    apiClient
      .get<PageDetail>(`/landing-pages/${selectedId}`)
      .then(({ data }) => setDetail(data))
      .catch(() => setDetail(null));
  }, [selectedId]);

  async function createPage() {
    if (!newName.trim() || !newSlug.trim()) return;
    await apiClient.post('/landing-pages', {
      name: newName.trim(),
      slug: newSlug.trim(),
      status: 'DRAFT',
      defaultLocale: 'es',
      abEnabled: false,
    });
    setCreating(false);
    setNewName('');
    setNewSlug('');
    reload();
  }

  async function savePage(d: PageDetail) {
    await apiClient.put(`/landing-pages/${d.id}`, {
      name: d.name,
      slug: d.slug,
      status: d.status,
      formId: d.formId,
      defaultLocale: d.defaultLocale,
      abEnabled: d.abEnabled,
      customDomain: d.customDomain,
    });
    reload();
  }

  async function deletePage(id: string) {
    await apiClient.delete(`/landing-pages/${id}`);
    if (selectedId === id) setSelectedId(null);
    reload();
  }

  if (loading) {
    return <p className="p-6 text-sm text-muted-foreground">{t('landing.admin.loading')}</p>;
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('landing.admin.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('landing.admin.subtitle')}</p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="lp-new">
          <Plus className="mr-2 h-4 w-4" />
          {t('landing.admin.newPage')}
        </Button>
      </div>

      {creating && (
        <Card>
          <CardHeader>
            <CardTitle>{t('landing.admin.newPage')}</CardTitle>
          </CardHeader>
          <div className="space-y-3 p-4">
            <Input
              placeholder={t('landing.admin.name')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <Input
              placeholder={t('landing.admin.slug')}
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
            />
            <div className="flex gap-2">
              <Button onClick={createPage}>{t('landing.admin.create')}</Button>
              <Button variant="ghost" onClick={() => setCreating(false)}>
                {t('landing.admin.cancel')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>{t('landing.admin.pages')}</CardTitle>
          </CardHeader>
          <ul className="divide-y divide-border">
            {pages.length === 0 && (
              <li className="p-4 text-sm text-muted-foreground">
                {t('landing.admin.empty')}
              </li>
            )}
            {pages.map((p) => (
              <li
                key={p.id}
                className={`flex cursor-pointer items-center justify-between p-3 text-sm hover:bg-muted/40 ${
                  selectedId === p.id ? 'bg-muted/60' : ''
                }`}
                onClick={() => setSelectedId(p.id)}
              >
                <span>
                  <span className="font-medium">{p.name}</span>
                  <span className="ml-2 text-xs text-muted-foreground">/{p.slug}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${
                      p.status === 'PUBLISHED'
                        ? 'bg-green-900/40 text-green-200'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {t(`landing.admin.status.${p.status}`)}
                  </span>
                  <button
                    aria-label={t('landing.admin.delete')}
                    onClick={(e) => {
                      e.stopPropagation();
                      void deletePage(p.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </Card>

        {detail ? (
          <PageEditor
            detail={detail}
            forms={forms}
            onChange={setDetail}
            onSave={savePage}
          />
        ) : (
          <Card>
            <div className="p-8 text-center text-sm text-muted-foreground">
              {t('landing.admin.selectHint')}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function PageEditor({
  detail,
  forms,
  onChange,
  onSave,
}: {
  detail: PageDetail;
  forms: FormOption[];
  onChange: (d: PageDetail) => void;
  onSave: (d: PageDetail) => void;
}) {
  const { t } = useTranslation();
  const [locale, setLocale] = useState<string>(detail.defaultLocale);
  const [variant, setVariant] = useState<string>('a');

  const current: ContentRow =
    detail.content.find((c) => c.locale === locale && c.abVariant === variant) ?? {
      locale,
      abVariant: variant,
      heroHeadline: '',
      heroImageUrl: null,
      bodyMd: null,
      thankYouMessage: null,
      ogTitle: null,
      ogDescription: null,
      ogImageUrl: null,
    };

  const [draft, setDraft] = useState<ContentRow>(current);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    setDraft(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, variant, detail]);

  async function saveContent() {
    await apiClient.put(`/landing-pages/${detail.id}/content`, draft);
    const { data } = await apiClient.get<PageDetail>(`/landing-pages/${detail.id}`);
    onChange(data);
  }

  async function generate() {
    setGenerating(true);
    try {
      const { data } = await apiClient.post<{ headline: string; body: string }>(
        `/landing-pages/${detail.id}/generate-copy`,
        { topic: detail.name, locale },
      );
      setDraft((d) => ({ ...d, heroHeadline: data.headline, bodyMd: data.body }));
    } catch {
      /* surfaced via empty result; author can retry */
    } finally {
      setGenerating(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{detail.name}</CardTitle>
      </CardHeader>
      <div className="space-y-5 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            {t('landing.admin.name')}
            <Input
              value={detail.name}
              onChange={(e) => onChange({ ...detail, name: e.target.value })}
            />
          </label>
          <label className="text-sm">
            {t('landing.admin.slug')}
            <Input
              value={detail.slug}
              onChange={(e) => onChange({ ...detail, slug: e.target.value })}
            />
          </label>
          <label className="text-sm">
            {t('landing.admin.form')}
            <select
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={detail.formId ?? ''}
              onChange={(e) =>
                onChange({ ...detail, formId: e.target.value || null })
              }
            >
              <option value="">{t('landing.admin.noForm')}</option>
              {forms.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.title}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            {t('landing.admin.status.label')}
            <select
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={detail.status}
              onChange={(e) => onChange({ ...detail, status: e.target.value })}
            >
              <option value="DRAFT">{t('landing.admin.status.DRAFT')}</option>
              <option value="PUBLISHED">{t('landing.admin.status.PUBLISHED')}</option>
            </select>
          </label>
          <label className="text-sm">
            {t('landing.admin.defaultLocale')}
            <select
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={detail.defaultLocale}
              onChange={(e) =>
                onChange({ ...detail, defaultLocale: e.target.value })
              }
            >
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-end gap-2 text-sm">
            <input
              type="checkbox"
              checked={detail.abEnabled}
              onChange={(e) =>
                onChange({ ...detail, abEnabled: e.target.checked })
              }
            />
            {t('landing.admin.abEnabled')}
          </label>
          <label className="text-sm sm:col-span-2">
            {t('landing.admin.customDomain')}
            <Input
              value={detail.customDomain ?? ''}
              onChange={(e) =>
                onChange({ ...detail, customDomain: e.target.value || null })
              }
            />
          </label>
        </div>
        <Button onClick={() => onSave(detail)} data-testid="lp-save-settings">
          {t('landing.admin.saveSettings')}
        </Button>

        <hr className="border-border" />

        <div className="flex flex-wrap items-center gap-3">
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={variant}
            onChange={(e) => setVariant(e.target.value)}
            disabled={!detail.abEnabled}
          >
            {VARIANTS.map((v) => (
              <option key={v} value={v}>
                {t('landing.admin.variant', { v: v.toUpperCase() })}
              </option>
            ))}
          </select>
          <Button variant="ghost" onClick={generate} disabled={generating}>
            <Sparkles className="mr-2 h-4 w-4" />
            {generating
              ? t('landing.admin.generating')
              : t('landing.admin.generateCopy')}
          </Button>
        </div>

        <div className="space-y-3">
          <label className="block text-sm">
            {t('landing.admin.heroHeadline')}
            <Input
              value={draft.heroHeadline}
              onChange={(e) =>
                setDraft({ ...draft, heroHeadline: e.target.value })
              }
            />
          </label>
          <label className="block text-sm">
            {t('landing.admin.heroImage')}
            <Input
              value={draft.heroImageUrl ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, heroImageUrl: e.target.value || null })
              }
            />
          </label>
          <label className="block text-sm">
            {t('landing.admin.body')}
            <textarea
              rows={6}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={draft.bodyMd ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, bodyMd: e.target.value || null })
              }
            />
          </label>
          <label className="block text-sm">
            {t('landing.admin.thankYou')}
            <Input
              value={draft.thankYouMessage ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, thankYouMessage: e.target.value || null })
              }
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              {t('landing.admin.ogTitle')}
              <Input
                value={draft.ogTitle ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, ogTitle: e.target.value || null })
                }
              />
            </label>
            <label className="text-sm">
              {t('landing.admin.ogDescription')}
              <Input
                value={draft.ogDescription ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, ogDescription: e.target.value || null })
                }
              />
            </label>
          </div>
          <Button onClick={saveContent} data-testid="lp-save-content">
            {t('landing.admin.saveContent')}
          </Button>
        </div>

        <LandingExperimentPanel pageId={detail.id} />
      </div>
    </Card>
  );
}
